/**
 * Centralized terminal `db_settings` access.
 *
 * Before Phase 4 the same `terminal.db_settings.find(s => s.name === X)` lookup
 * (followed by `value === 'true'`) was copy-pasted across 13+ computeds in
 * StandardOrder.vue / StandardOrderCheckout.vue. Each call did a linear scan of
 * the settings array. This module collapses that into one helper backed by a
 * small reference-keyed cache so repeated reads are O(1) lookups against a Map.
 *
 * The functions are pure (terminal in, value out) and never touch Vuex, so they
 * are unit-testable in isolation.
 *
 * Behavior note: `isTerminalSettingEnabled` mirrors the dominant legacy check
 * (`setting ? setting.value === 'true' : false`). Settings whose legacy code
 * also accepted a real boolean `true` (e.g. `group_qty`) should keep using
 * `getTerminalSetting` + their own check, or pass `acceptBoolean: true`.
 */

// Single-entry cache keyed on the db_settings array *reference*. The terminal /
// its db_settings array is replaced (not mutated in place) on config refresh, so
// a reference check is enough to invalidate. Keeping it module-level means every
// component shares the same built map for the active terminal.
let _cachedSource = null;
let _cachedMap = null;

function getSettingsMap(terminal) {
  const settings = terminal && terminal.db_settings;
  if (!Array.isArray(settings)) return null;
  if (settings !== _cachedSource) {
    const map = new Map();
    for (const setting of settings) {
      if (setting && setting.name != null && !map.has(setting.name)) {
        map.set(setting.name, setting);
      }
    }
    _cachedSource = settings;
    _cachedMap = map;
  }
  return _cachedMap;
}

/**
 * Return the raw db_setting object for `name`, or undefined. Drop-in replacement
 * for `terminal.db_settings.find(s => s.name === name)`.
 */
export function getTerminalSetting(terminal, name) {
  const map = getSettingsMap(terminal);
  if (!map) return undefined;
  return map.get(name);
}

/**
 * Return the raw `.value` of a setting (or `fallback` when absent).
 */
export function getTerminalSettingValue(terminal, name, fallback = undefined) {
  const setting = getTerminalSetting(terminal, name);
  return setting ? setting.value : fallback;
}

/**
 * Truthiness check matching the legacy `setting ? setting.value === 'true' : false`
 * pattern. Pass `{ acceptBoolean: true }` to also treat a real boolean `true` as
 * enabled (legacy `group_qty` semantics).
 */
export function isTerminalSettingEnabled(terminal, name, { acceptBoolean = false } = {}) {
  const setting = getTerminalSetting(terminal, name);
  if (!setting) return false;
  if (acceptBoolean && setting.value === true) return true;
  return setting.value === "true";
}

/**
 * Test-only / config-refresh hook to drop the cached map.
 */
export function _resetTerminalSettingsCache() {
  _cachedSource = null;
  _cachedMap = null;
}
